/**
 * TaskDetailPage — view/edit task, history, comments, WhatsApp & email notifications
 */
import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Trash2, Edit2, Save, MessageCircle,
  Send, Bell, Mail, Clock, ChevronDown, ChevronUp,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTMAuth } from '@/contexts/TMAuthContext';
import {
  getTask, updateTask, deleteTask, getApprovedTMUsers,
  addTaskHistory, subscribeTaskHistory,
  buildWhatsAppUrl, taskAssignedWhatsAppMessage, taskUpdatedWhatsAppMessage, taskReminderWhatsAppMessage,
} from '@/services/tmService';
import type {
  Task, TaskStatus, TaskPriority, TMUser, TaskHistoryEntry, TaskHistoryChange,
} from '@/types/tm.types';
import {
  TASK_STATUS_COLORS, TASK_STATUS_LABELS, TASK_STATUSES,
  TASK_PRIORITY_COLORS, TASK_PRIORITY_LABELS, TASK_PRIORITIES,
  TASK_HISTORY_LABELS,
} from '@/types/tm.types';
import { cn } from '@/utils/cn';

const APP_URL = 'https://www.wombcat.com/tm';

function daysUntil(date: Date | null) {
  if (!date) return null;
  return Math.ceil((date.getTime() - Date.now()) / 86400000);
}

function formatTime(date: Date | null) {
  if (!date) return '';
  return date.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── History timeline ──────────────────────────────────────────────────────────

function HistoryTimeline({ entries }: { entries: TaskHistoryEntry[] }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? entries : entries.slice(0, 5);

  if (entries.length === 0) return (
    <p className="text-xs text-gray-400 text-center py-4">No history yet.</p>
  );

  return (
    <div className="space-y-0">
      {visible.map((e, i) => (
        <div key={e.id} className="flex gap-3">
          {/* Timeline line */}
          <div className="flex flex-col items-center">
            <div className={cn(
              'w-2 h-2 rounded-full mt-1 shrink-0',
              e.action === 'comment' ? 'bg-indigo-400' :
              e.action === 'created' ? 'bg-emerald-400' : 'bg-gray-300',
            )} />
            {i < visible.length - 1 && <div className="w-px flex-1 bg-gray-100 my-1" />}
          </div>
          <div className="pb-4 min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-semibold text-gray-700">
                {e.userName} · <span className="font-normal text-gray-500">{TASK_HISTORY_LABELS[e.action]}</span>
              </p>
              <span className="text-[10px] text-gray-400 shrink-0">{formatTime(e.createdAt)}</span>
            </div>
            {e.comment && (
              <p className="mt-1 text-sm text-gray-700 bg-indigo-50 rounded-xl px-3 py-2">{e.comment}</p>
            )}
            {e.changes.length > 0 && (
              <div className="mt-1 space-y-0.5">
                {e.changes.map((c, ci) => (
                  <p key={ci} className="text-xs text-gray-500">
                    <span className="font-medium capitalize">{c.field}</span>:{' '}
                    <span className="line-through text-gray-400">{c.oldValue ?? '—'}</span>
                    {' → '}
                    <span className="text-gray-700">{c.newValue ?? '—'}</span>
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
      {entries.length > 5 && (
        <button onClick={() => setExpanded(p => !p)}
          className="flex items-center gap-1 text-xs text-indigo-500 font-medium mt-1">
          {expanded ? <><ChevronUp className="h-3 w-3" /> Show less</> : <><ChevronDown className="h-3 w-3" /> Show {entries.length - 5} more</>}
        </button>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function TaskDetailPage() {
  const { id }         = useParams<{ id: string }>();
  const navigate       = useNavigate();
  const { tmUser, isTMAdmin } = useTMAuth();

  const [task,    setTask]    = useState<Task | null>(null);
  const [members, setMembers] = useState<TMUser[]>([]);
  const [history, setHistory] = useState<TaskHistoryEntry[]>([]);
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [comment, setComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Editable fields
  const [title,         setTitle]         = useState('');
  const [description,   setDescription]   = useState('');
  const [status,        setStatus]        = useState<TaskStatus>('todo');
  const [priority,      setPriority]      = useState<TaskPriority>('medium');
  const [progress,      setProgress]      = useState(0);
  const [dueDate,       setDueDate]       = useState('');
  const [assignedToUid, setAssignedToUid] = useState('');

  const prevTask = useRef<Task | null>(null);

  // Determine if current user can edit
  const isAssignee = task?.assignedTo?.uid === tmUser?.uid;
  const canEdit    = isTMAdmin || (tmUser?.permissions?.editTask ?? false);
  const canUpdate  = isTMAdmin || isAssignee || (tmUser?.permissions?.updateTask ?? false);
  const canAssign  = isTMAdmin || (tmUser?.permissions?.assignTask ?? false);
  const canDelete  = isTMAdmin;

  useEffect(() => {
    if (!id) return;
    getTask(id).then(t => {
      if (!t) return;
      setTask(t);
      prevTask.current = t;
      populateForm(t);
    });
    // Always load members — needed for assign dropdown and WhatsApp phone lookup
    getApprovedTMUsers().then(setMembers).catch(() => {
      console.warn('Could not load members list');
    });
    const unsub = subscribeTaskHistory(id, setHistory);
    return unsub;
  }, [id]);

  function populateForm(t: Task) {
    setTitle(t.title);
    setDescription(t.description ?? '');
    setStatus(t.status);
    setPriority(t.priority);
    setProgress(t.progress);
    setDueDate(t.dueDate ? t.dueDate.toISOString().split('T')[0] : '');
    setAssignedToUid(t.assignedTo?.uid ?? '');
  }

  // ── Save with history ──────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!id || !task || !tmUser) return;
    setSaving(true);
    const changes: TaskHistoryChange[] = [];

    if (title !== task.title)
      changes.push({ field: 'title', oldValue: task.title, newValue: title });
    if (status !== task.status)
      changes.push({ field: 'status', oldValue: TASK_STATUS_LABELS[task.status], newValue: TASK_STATUS_LABELS[status] });
    if (priority !== task.priority)
      changes.push({ field: 'priority', oldValue: TASK_PRIORITY_LABELS[task.priority], newValue: TASK_PRIORITY_LABELS[priority] });
    if (progress !== task.progress)
      changes.push({ field: 'progress', oldValue: `${task.progress}%`, newValue: `${progress}%` });
    if (dueDate !== (task.dueDate ? task.dueDate.toISOString().split('T')[0] : ''))
      changes.push({ field: 'due date', oldValue: task.dueDate?.toLocaleDateString('en-IN') ?? null, newValue: dueDate || null });

    const assignee    = members.find(m => m.uid === assignedToUid);
    const newAssignee = assignee
      ? { uid: assignee.uid, name: assignee.name, email: assignee.email, phone: assignee.phone }
      : task.assignedTo;

    if (assignedToUid !== (task.assignedTo?.uid ?? '') && assignee)
      changes.push({ field: 'assigned to', oldValue: task.assignedTo?.name ?? 'Unassigned', newValue: assignee.name });

    try {
      await updateTask(id, {
        title, description: description || null, status, priority, progress,
        dueDate: dueDate ? new Date(dueDate) : null,
        assignedTo: newAssignee,
      });

      const action = changes.some(c => c.field === 'status') ? 'status_changed'
        : changes.some(c => c.field === 'assigned to') ? 'reassigned'
        : changes.some(c => c.field === 'progress') ? 'progress_changed'
        : 'edited';

      if (changes.length > 0) {
        await addTaskHistory(id, {
          userId: tmUser.uid, userName: tmUser.name, action, changes,
        });
      }

      setTask(prev => prev ? { ...prev, title, description: description || null, status, priority, progress, dueDate: dueDate ? new Date(dueDate) : null } : prev);
      toast.success('Task updated');
      setEditing(false);
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  // ── Quick status (no edit mode) ───────────────────────────────────────────

  const handleStatusChange = async (s: TaskStatus) => {
    if (!id || !task || !tmUser) return;
    const old = status;
    setStatus(s);
    await updateTask(id, { status: s });
    await addTaskHistory(id, {
      userId: tmUser.uid, userName: tmUser.name, action: 'status_changed',
      changes: [{ field: 'status', oldValue: TASK_STATUS_LABELS[old], newValue: TASK_STATUS_LABELS[s] }],
    });
    toast.success('Status updated');
  };

  // ── Comment ───────────────────────────────────────────────────────────────

  const handleComment = async () => {
    if (!id || !tmUser || !comment.trim()) return;
    setSubmittingComment(true);
    await addTaskHistory(id, {
      userId: tmUser.uid, userName: tmUser.name, action: 'comment', comment: comment.trim(),
    });
    setComment('');
    toast.success('Comment added');
    setSubmittingComment(false);
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!id || !confirm('Delete this task?')) return;
    await deleteTask(id);
    toast.success('Task deleted');
    navigate('/tm/tasks');
  };

  // ── Email notification ────────────────────────────────────────────────────

  const handleSendEmail = async () => {
    if (!task?.assignedTo?.email) { toast.error('No assignee email'); return; }
    setSendingEmail(true);
    try {
      const res = await fetch(
        `https://us-central1-wombcat-cms.cloudfunctions.net/sendTaskNotificationHttp`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId: id, type: 'reminder' }),
        }
      );
      if (res.ok) toast.success('Email sent');
      else toast.error('Failed to send email');
    } catch {
      toast.error('Failed to send email');
    }
    setSendingEmail(false);
  };

  // ── WhatsApp helpers ──────────────────────────────────────────────────────

  const openWhatsApp = (message: string) => {
    if (!task?.assignedTo) { toast.error('No assignee set'); return; }
    // Phone is stored on the task's assignedTo ref; fall back to members list
    const phone =
      task.assignedTo.phone ||
      members.find(m => m.uid === task.assignedTo!.uid)?.phone ||
      '';
    if (!phone) { toast.error('Assignee has no phone number saved'); return; }
    window.open(buildWhatsAppUrl(phone, message), '_blank');
  };

  const sendAssignedWA = () => openWhatsApp(
    taskAssignedWhatsAppMessage(task!.assignedTo!.name, task!.title, task!.dueDate, APP_URL)
  );

  const sendUpdateWA = () => openWhatsApp(
    taskUpdatedWhatsAppMessage(task!.assignedTo!.name, task!.title, `Status: ${TASK_STATUS_LABELS[status]}`, APP_URL)
  );

  const sendReminderWA = () => {
    const d = daysUntil(task?.dueDate ?? null);
    openWhatsApp(taskReminderWhatsAppMessage(task!.assignedTo!.name, task!.title, d ?? 0, APP_URL));
  };

  // ─────────────────────────────────────────────────────────────────────────

  if (!task) return (
    <div className="flex items-center justify-center h-48 text-gray-400 text-sm">Loading…</div>
  );

  const d = daysUntil(task.dueDate);
  const inputCls = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400';

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div className="flex items-center gap-2">
          {canEdit && !editing && (
            <button onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
              <Edit2 className="h-4 w-4" /> Edit
            </button>
          )}
          {editing && (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold active:scale-95 transition-all disabled:opacity-60">
              <Save className="h-4 w-4" />
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
          {canDelete && (
            <button onClick={handleDelete} className="p-2 rounded-xl text-red-500 hover:bg-red-50 transition-colors">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Main card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">

        {/* Title */}
        {editing ? (
          <input value={title} onChange={e => setTitle(e.target.value)} className={inputCls} />
        ) : (
          <h1 className="text-lg font-bold text-gray-900">{task.title}</h1>
        )}

        {/* Status pills */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Status</p>
          <div className="flex gap-2 flex-wrap">
            {TASK_STATUSES.map(s => (
              <button key={s} type="button"
                onClick={() => canUpdate && !editing && handleStatusChange(s)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-semibold border transition-all',
                  status === s
                    ? cn(TASK_STATUS_COLORS[s], 'border-current ring-2 ring-offset-1 ring-current/30')
                    : 'border-gray-200 text-gray-400',
                  !canUpdate && 'cursor-default',
                )}>
                {TASK_STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>

        {/* Priority */}
        {editing ? (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Priority</p>
            <div className="flex gap-2 flex-wrap">
              {TASK_PRIORITIES.map(p => (
                <button key={p} type="button" onClick={() => setPriority(p)}
                  className={cn('px-3 py-1.5 rounded-full text-xs font-semibold border transition-all capitalize',
                    priority === p ? cn(TASK_PRIORITY_COLORS[p], 'ring-2 ring-offset-1 ring-current/30') : 'border-gray-200 text-gray-400')}>
                  {TASK_PRIORITY_LABELS[p]}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <span className={cn('inline-block text-xs font-bold px-3 py-1 rounded-full', TASK_PRIORITY_COLORS[task.priority])}>
            {TASK_PRIORITY_LABELS[task.priority]} Priority
          </span>
        )}

        {/* Progress */}
        {canUpdate && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Progress</p>
              <span className="text-sm font-bold text-indigo-600">{progress}%</span>
            </div>
            <input type="range" min={0} max={100} step={5} value={progress}
              onChange={e => setProgress(Number(e.target.value))}
              className="w-full accent-indigo-600" />
          </div>
        )}

        {/* Description */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Description</p>
          {editing ? (
            <textarea rows={4} value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Add details…" className={inputCls} />
          ) : (
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{task.description ?? '—'}</p>
          )}
        </div>

        {/* Meta grid */}
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Assigned to</p>
            {canAssign && members.length > 0 ? (
              /* Quick-assign dropdown — always visible for admins/users with assignTask */
              <select
                value={assignedToUid}
                onChange={async e => {
                  const uid = e.target.value;
                  setAssignedToUid(uid);
                  if (!id || !tmUser) return;
                  const assignee = members.find(m => m.uid === uid);
                  const newAssignee = assignee
                    ? { uid: assignee.uid, name: assignee.name, email: assignee.email, phone: assignee.phone }
                    : null;
                  const oldName = task.assignedTo?.name ?? 'Unassigned';
                  await updateTask(id, { assignedTo: newAssignee });
                  await addTaskHistory(id, {
                    userId: tmUser.uid, userName: tmUser.name,
                    action: task.assignedTo ? 'reassigned' : 'assigned',
                    changes: [{ field: 'assigned to', oldValue: oldName, newValue: assignee?.name ?? 'Unassigned' }],
                  });
                  setTask(prev => prev ? { ...prev, assignedTo: newAssignee } : prev);
                  toast.success(assignee ? `Assigned to ${assignee.name}` : 'Unassigned');
                }}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-full focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white">
                <option value="">Unassigned</option>
                {members.map(m => <option key={m.uid} value={m.uid}>{m.name}</option>)}
              </select>
            ) : editing ? (
              <select value={assignedToUid} onChange={e => setAssignedToUid(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-full focus:outline-none focus:ring-1 focus:ring-indigo-400">
                <option value="">Unassigned</option>
                {members.map(m => <option key={m.uid} value={m.uid}>{m.name}</option>)}
              </select>
            ) : (
              <p className="text-sm font-medium text-gray-800">{task.assignedTo?.name ?? 'Unassigned'}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Assigned by</p>
            <p className="text-sm font-medium text-gray-800">{task.assignedBy?.name ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Due date</p>
            {editing ? (
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-full focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            ) : (
              <p className={cn('text-sm font-medium',
                d !== null && d < 0 ? 'text-red-500' : d !== null && d <= 3 ? 'text-amber-600' : 'text-gray-800')}>
                {task.dueDate
                  ? task.dueDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                  : '—'}
                {d !== null && d < 0 && ` (${Math.abs(d)}d overdue)`}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Reminder</p>
            <p className="text-sm font-medium text-gray-800">
              {task.reminderDays > 0 ? `${task.reminderDays}d before` : 'None'}
            </p>
          </div>
        </div>

        {task.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {task.tags.map(tag => (
              <span key={tag} className="text-xs bg-indigo-50 text-indigo-600 px-2.5 py-1 rounded-full font-medium">#{tag}</span>
            ))}
          </div>
        )}
      </div>

      {/* Notification buttons — visible to any approved TM user when task has an assignee */}
      {task.assignedTo && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Notify Assignee</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={sendAssignedWA}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500 text-white text-xs font-semibold active:scale-95 transition-all">
              <MessageCircle className="h-3.5 w-3.5" /> Task Assigned (WA)
            </button>
            <button onClick={sendUpdateWA}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500 text-white text-xs font-semibold active:scale-95 transition-all">
              <MessageCircle className="h-3.5 w-3.5" /> Send Update (WA)
            </button>
            <button onClick={sendReminderWA}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600 text-white text-xs font-semibold active:scale-95 transition-all">
              <Bell className="h-3.5 w-3.5" /> Reminder (WA)
            </button>
            <button onClick={handleSendEmail} disabled={sendingEmail}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-600 text-white text-xs font-semibold active:scale-95 transition-all disabled:opacity-60">
              <Mail className="h-3.5 w-3.5" />
              {sendingEmail ? 'Sending…' : 'Send Email'}
            </button>
          </div>
          <p className="text-[10px] text-gray-400 mt-2">WhatsApp buttons open your WhatsApp with a pre-filled message ready to send.</p>
        </div>
      )}

      {/* Comment box */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Add Remark / Comment</p>
        <div className="flex gap-2">
          <textarea
            rows={2}
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Write a comment or update remark…"
            className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
          />
          <button onClick={handleComment} disabled={submittingComment || !comment.trim()}
            className="self-end p-3 rounded-xl bg-indigo-600 text-white active:scale-95 transition-all disabled:opacity-50">
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="h-4 w-4 text-gray-400" />
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">History</p>
          <span className="ml-auto text-xs text-gray-400">{history.length} entries</span>
        </div>
        <HistoryTimeline entries={history} />
      </div>

    </div>
  );
}
